import { QueryClient } from '@tanstack/react-query';
import { NETWORK_PRICES_QUERY_KEY } from './hooks/useNetworkPricesQuery';
import { getNetworkPrices } from './services';
import { LineKey, NetworkPriceLine, NetworkPricesPage } from './types';
import { getLineFilter, getLineKey } from './utils';

export type LineReload = { isLatest: () => boolean; finish: () => void };

export const NETWORK_PRICES_QUERY_FILTER = { queryKey: [NETWORK_PRICES_QUERY_KEY] };
const lineReloadsByQueryClient = new WeakMap<QueryClient, Map<LineKey, symbol>>();

// Only the latest save of a line, cluster or network, may write its reload into the cache.
export const startLineReload = (queryClient: QueryClient, lineKey: LineKey): LineReload => {
  const reloadId = Symbol(lineKey);
  const lineReloads = lineReloadsByQueryClient.get(queryClient) ?? new Map<LineKey, symbol>();
  lineReloadsByQueryClient.set(queryClient, lineReloads);
  lineReloads.set(lineKey, reloadId);
  const isLatest = (): boolean => lineReloads.get(lineKey) === reloadId;

  return {
    isLatest,
    finish: () => {
      if (isLatest()) lineReloads.delete(lineKey);
    },
  };
};

// A line missing from the reload left the grid (e.g. applied elsewhere): drop it and fix the total.
const replaceReloadedLine = (page: NetworkPricesPage, lineKey: LineKey, reloadedLine: NetworkPriceLine | undefined): NetworkPricesPage => {
  const content = reloadedLine
    ? page.content.map(line => (getLineKey(line) === lineKey ? reloadedLine : line))
    : page.content.filter(line => getLineKey(line) !== lineKey);

  return { ...page, content, totalElements: page.totalElements - (page.content.length - content.length) };
};

export const reloadNetworkLine = async (queryClient: QueryClient, line: NetworkPriceLine, lineReload: LineReload): Promise<void> => {
  const lineKey = getLineKey(line);
  try {
    const page = await getNetworkPrices({ page: 0, size: 1 }, getLineFilter(line));
    if (!lineReload.isLatest()) return;
    const reloadedLine = page.content[0];
    queryClient.setQueriesData<NetworkPricesPage>(NETWORK_PRICES_QUERY_FILTER, cached => cached && replaceReloadedLine(cached, lineKey, reloadedLine));
    // Refetch removed lines to reconcile pagination, and cancelled first fetches without cached data.
    await queryClient.invalidateQueries({ ...NETWORK_PRICES_QUERY_FILTER, predicate: query => !reloadedLine || query.state.data === undefined });
  } catch {
    if (lineReload.isLatest()) await queryClient.invalidateQueries(NETWORK_PRICES_QUERY_FILTER);
  }
};
